import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, MapPin, Phone, Mail } from "lucide-react";
import { COMPANY, SOCIALS, APPLY_PATH, LOGO_URL } from "@/data/content";

const ICONS = { Facebook, Instagram, LinkedIn: Linkedin, YouTube: Youtube };

const PROGRAM_LINKS = [
  { label: "Fleet Partner Program", href: "/#program" },
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Revenue Opportunity", href: "/#revenue" },
  { label: "Fleet Partner Application", href: APPLY_PATH },
  { label: "Investor Portal", href: "/investors" },
  { label: "Contact", href: "/#contact" },
];

const LEGAL_LINKS = [
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Terms and Conditions", href: "/terms-and-conditions" },
  { label: "Accessibility", href: "/accessibility" },
];

export const Footer = () => (
  <footer id="contact" className="relative border-t border-black/[0.06] bg-ink" data-testid="site-footer">
    <div className="container-x grid gap-12 py-16 lg:grid-cols-[1.4fr_1fr_1fr] lg:py-20">
      <div>
        <div className="mb-5">
          <img src={LOGO_URL} alt="Xtreme Exotics Watersports" className="h-24 w-auto drop-shadow-[0_0_14px_rgba(0,252,245,0.22)]" />
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-muted">
          A premium marine-fleet company partnering with qualified waterfront operators to bring the Jet Car experience to new markets.
        </p>
        <div className="mt-6 space-y-2.5 text-sm text-muted">
          <p className="flex items-start gap-2.5" data-testid="footer-address">
            <MapPin size={15} className="mt-0.5 shrink-0 text-aqua" />
            <span>
              {COMPANY.name}
              <br />
              {COMPANY.address1}
              <br />
              {COMPANY.address2}
            </span>
          </p>
          <a href={COMPANY.phoneHref} className="flex items-center gap-2.5 transition-colors hover:text-aqua" data-testid="footer-phone">
            <Phone size={15} className="shrink-0 text-aqua" /> {COMPANY.phone}
          </a>
          <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2.5 transition-colors hover:text-aqua" data-testid="footer-email">
            <Mail size={15} className="shrink-0 text-aqua" /> {COMPANY.email}
          </a>
        </div>
        <div className="mt-6 flex gap-3">
          {SOCIALS.map((s) => {
            const Icon = ICONS[s.label];
            return (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Xtreme Exotics on ${s.label}`}
                data-testid={`footer-social-${s.label.toLowerCase()}`}
                className="grid h-10 w-10 place-items-center rounded-full border border-black/10 text-muted transition-[color,border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-aqua/50 hover:text-aqua"
              >
                <Icon size={16} />
              </a>
            );
          })}
        </div>
      </div>

      <nav aria-label="Program">
        <h3 className="eyebrow mb-5">Program</h3>
        <ul className="space-y-3">
          {PROGRAM_LINKS.map((l) => (
            <li key={l.label}>
              <Link to={l.href} className="text-sm text-muted transition-colors hover:text-aqua" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <nav aria-label="Legal">
        <h3 className="eyebrow mb-5">Legal</h3>
        <ul className="space-y-3">
          {LEGAL_LINKS.map((l) => (
            <li key={l.label}>
              <Link to={l.href} className="text-sm text-muted transition-colors hover:text-aqua" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </div>

    <div className="border-t border-black/[0.06]">
      <div className="container-x flex flex-col items-center justify-between gap-3 py-6 text-xs text-muted/70 sm:flex-row">
        <p>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</p>
        <p className="font-mono tracking-widest">Fleet Partner Program · B2B Inquiries Only</p>
      </div>
    </div>
  </footer>
);
